<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import format from 'date-fns/format';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Table from 'dashboard/components/table/Table.vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { h } from 'vue';
import ContactAPI from 'dashboard/api/contacts';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const agents = useMapGetter('agents/getVerifiedAgents');
const teams = useMapGetter('teams/getTeams');

const isLoading = ref(false);
const isExporting = ref(false);
const reportData = ref(null);

const responsibleAgentId = ref(route.query.responsibleAgentId?.toString() || '');
const teamId = ref(route.query.teamId?.toString() || '');

const agentOptions = computed(() => [
  { value: '', label: t('CONTACT_PORTFOLIO.FILTERS.ALL_AGENTS') },
  ...(agents.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  })),
]);

const teamOptions = computed(() => [
  { value: '', label: t('CONTACT_PORTFOLIO.FILTERS.ALL_TEAMS') },
  ...(teams.value || []).map(team => ({
    value: String(team.id),
    label: team.name,
  })),
]);

const buildApiParams = () => ({
  responsibleAgentId: responsibleAgentId.value || undefined,
  teamId: teamId.value || undefined,
});

const syncQueryParams = () => {
  const query = { ...route.query };
  if (responsibleAgentId.value) {
    query.responsibleAgentId = responsibleAgentId.value;
  } else {
    delete query.responsibleAgentId;
  }
  if (teamId.value) {
    query.teamId = teamId.value;
  } else {
    delete query.teamId;
  }
  router.replace({ query });
};

const fetchReport = async () => {
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.portfolioDistribution(buildApiParams());
    reportData.value = data;
  } catch {
    useAlert(t('CONTACT_PORTFOLIO_DISTRIBUTION.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handleExport = async () => {
  isExporting.value = true;
  try {
    const response = await ContactAPI.portfolioDistributionExport(buildApiParams());
    const fileName = `portfolio-distribution-${format(new Date(), 'dd-MM-yyyy')}.csv`;
    downloadCsvFile(fileName, response.data);
  } catch {
    useAlert(t('CONTACT_PORTFOLIO_DISTRIBUTION.EXPORT_ERROR'));
  } finally {
    isExporting.value = false;
  }
};

const columnHelper = createColumnHelper();

const defaultCell = cellProps =>
  h(
    'span',
    { class: 'text-n-slate-12' },
    cellProps.getValue() ?? '---'
  );

const countCell = cellProps =>
  h(
    'span',
    { class: 'text-n-slate-12 font-medium' },
    Number(cellProps.getValue() || 0).toLocaleString()
  );

const responsibleColumns = [
  columnHelper.accessor('name', {
    header: () => t('CONTACT_PORTFOLIO_DISTRIBUTION.RESPONSIBLE'),
    cell: defaultCell,
  }),
  columnHelper.accessor('count', {
    header: () => t('CONTACT_PORTFOLIO_DISTRIBUTION.COUNT'),
    cell: countCell,
  }),
];

const departmentColumns = [
  columnHelper.accessor('name', {
    header: () => t('CONTACT_PORTFOLIO_DISTRIBUTION.DEPARTMENT'),
    cell: defaultCell,
  }),
  columnHelper.accessor('count', {
    header: () => t('CONTACT_PORTFOLIO_DISTRIBUTION.COUNT'),
    cell: countCell,
  }),
];

const dddColumns = [
  columnHelper.accessor('ddd', {
    header: () => t('CONTACT_PORTFOLIO_DISTRIBUTION.DDD'),
    cell: defaultCell,
  }),
  columnHelper.accessor('count', {
    header: () => t('CONTACT_PORTFOLIO_DISTRIBUTION.COUNT'),
    cell: countCell,
  }),
];

const responsibleTable = useVueTable({
  get data() {
    return reportData.value?.by_responsible || [];
  },
  columns: responsibleColumns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const departmentTable = useVueTable({
  get data() {
    return reportData.value?.by_department || [];
  },
  columns: departmentColumns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const dddTable = useVueTable({
  get data() {
    return reportData.value?.by_ddd || [];
  },
  columns: dddColumns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

watch([responsibleAgentId, teamId], () => {
  syncQueryParams();
  fetchReport();
});

onMounted(async () => {
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
  ]);
  fetchReport();
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-surface-1">
    <header class="px-6 py-4 border-b border-n-weak bg-n-solid-1">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-lg font-medium text-n-slate-12">
            {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="t('CONTACT_PORTFOLIO_DISTRIBUTION.EXPORT')"
          icon="i-ph-download-simple"
          size="sm"
          :is-loading="isExporting"
          :disabled="isLoading || isExporting"
          @click="handleExport"
        />
      </div>
    </header>

    <main class="flex flex-col flex-1 gap-4 px-6 py-4">
      <div
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 grid grid-cols-1 gap-4 md:grid-cols-2"
      >
        <div class="flex flex-col gap-1.5 min-w-0">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PORTFOLIO.FILTERS.RESPONSIBLE_AGENT') }}
          </label>
          <ComboBox
            v-model="responsibleAgentId"
            :options="agentOptions"
            :placeholder="t('CONTACT_PORTFOLIO.FILTERS.RESPONSIBLE_AGENT_PLACEHOLDER')"
          />
        </div>

        <div class="flex flex-col gap-1.5 min-w-0">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PORTFOLIO.FILTERS.DEPARTMENT') }}
          </label>
          <ComboBox
            v-model="teamId"
            :options="teamOptions"
            :placeholder="t('CONTACT_PORTFOLIO.FILTERS.DEPARTMENT_PLACEHOLDER')"
          />
        </div>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center gap-2 py-16">
        <Spinner />
        <span class="text-n-slate-11">{{ t('CONTACT_PORTFOLIO_DISTRIBUTION.LOADING') }}</span>
      </div>

      <template v-else-if="reportData">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 px-6 py-5 shadow outline-1 outline outline-n-container"
          >
            <p class="text-sm text-n-slate-11">
              {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.TOTAL_CONTACTS') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12 mt-2">
              {{ Number(reportData.total_contacts || 0).toLocaleString() }}
            </p>
          </div>
          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 px-6 py-5 shadow outline-1 outline outline-n-container"
          >
            <p class="text-sm text-n-slate-11">
              {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.WITHOUT_RESPONSIBLE') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12 mt-2">
              {{ Number(reportData.without_responsible || 0).toLocaleString() }}
            </p>
          </div>
          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 px-6 py-5 shadow outline-1 outline outline-n-container"
          >
            <p class="text-sm text-n-slate-11">
              {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.DEPARTMENTS') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12 mt-2">
              {{ (reportData.by_department || []).length.toLocaleString() }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <section
            class="rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow outline-1 outline outline-n-container"
          >
            <h3 class="text-sm font-medium text-n-slate-12 mb-4">
              {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.BY_RESPONSIBLE') }}
            </h3>
            <Table :table="responsibleTable" />
          </section>

          <section
            class="rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow outline-1 outline outline-n-container"
          >
            <h3 class="text-sm font-medium text-n-slate-12 mb-4">
              {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.BY_DEPARTMENT') }}
            </h3>
            <Table :table="departmentTable" />
          </section>

          <section
            class="rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow outline-1 outline outline-n-container xl:col-span-2"
          >
            <h3 class="text-sm font-medium text-n-slate-12 mb-4">
              {{ t('CONTACT_PORTFOLIO_DISTRIBUTION.BY_DDD') }}
            </h3>
            <Table :table="dddTable" />
          </section>
        </div>
      </template>
    </main>
  </div>
</template>
